import {
  CalendarIcon,
  CheckboxActiveIcon,
  CheckedIcon,
  CustomFieldIcon,
  DropdownBorderedIcon,
  LinkTicketIcon,
  LongTextInputIcon,
  ReleaseIdIcon,
  RequiredIcon,
  TagIcon,
  TextInputIcon,
  TicketIdIcon,
  UserIdIcon,
} from "app/components/Icons";
import { HarshIcon } from "app/components/Icons/HarshIcon";
import MembersIcons from "app/components/Icons/MembersIcon";
import { SettingIcon } from "app/components/Icons/Setting";
import TicketsIcon from "app/components/Icons/TicketsIcon";
import { TableHeader } from "app/components/Table/types";
import { contentJson } from "app/containers/Features/containers/TeamSettings/container/Projects/constants";
import React from "react";
import { ITicketTypes } from "../WorkItems/types";
import RenderTicketTypes from "./component/RenderTicketTypes";
import {
  CustomFieldItem,
  CustomFieldType,
  CustomSubMenu,
  ITileProps,
} from "./types";
const {
  tableHeader,
  mandatoryField,
  extraParameters,
  fieldType,
  fieldTypeFields,
} = contentJson.customfds;

const headers: TableHeader[] = [
  {
    name: tableHeader.type,
    id: "type",
    align: "left",
    sortable: true,
    width: 350,
  },
  {
    name: tableHeader.name,
    id: "name",
    align: "left",
    sortable: true,
    width: 350,
  },
  {
    name: tableHeader.ticketTypes,
    id: "ticketType",
    align: "left",
    sortable: true,
    width: 350,
  },
  {
    name: tableHeader.permission,
    id: "permission",
    align: "left",
    sortable: true,
    width: 250,
  },
  {
    name: tableHeader.mandatory,
    id: "mandatory",
    align: "left",
    sortable: true,
    width: 180,
  },
];
const getCustomFieldsMenus = (form?: CustomFieldItem): ITileProps[] => {
  const base: ITileProps[] = [
    {
      title: fieldType,
      icon: <CustomFieldIcon size={20} color="#79808A" />,
      order: 1,
      isLink: true,
      link: CustomSubMenu.FIELD_TYPE,
    },
    {
      title: tableHeader.ticketTypes,
      icon: <TicketsIcon size={20} color="#79808A" />,
      order: 3,
      isLink: true,
      link: CustomSubMenu.TICKET_TYPES,
    },
    {
      title: extraParameters,
      icon: <SettingIcon size={22} />,
      order: 4,
      isLink: true,
      link: CustomSubMenu.EXTRA_PARAMETER,
    },
    {
      title: tableHeader.permission,
      icon: <MembersIcons size="20" color="#79808A" />,
      order: 5,
      isLink: true,
      link: CustomSubMenu.PERMISSION,
    },
    {
      title: mandatoryField,
      icon: <RequiredIcon size="20" color="#79808A" />,
      order: 5,
      isLink: false,
    },
  ];

  // Add Dropdown options tile when datatype is DROPDOWN
  if (form?.dataType === CustomFieldType.DROPDOWN) {
    base.push({
      title: contentJson.customfds.dropdownValues.title || "Dropdown values",
      icon: <DropdownBorderedIcon />,
      order: 2,
      isLink: true,
      link: CustomSubMenu.DROPDOWN,
    });
  }

  // Add Labels tile when datatype is LABEL
  if (form?.dataType === CustomFieldType.LABEL) {
    base.push({
      title: contentJson.customfds.labelValues.title || "Labels values",
      icon: <TagIcon />,
      order: 2,
      isLink: true,
      link: CustomSubMenu.LABEL,
    });
  }

  // return menus sorted by `order` ascending so UI renders in configured order
  return base.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

const getCustomFieldTypeIcons = (val: CustomFieldType, size?: number) => {
  switch (val) {
    case CustomFieldType.TEXT:
      return <TextInputIcon />;
    case CustomFieldType.LONG_TEXT:
      return <LongTextInputIcon />;
    case CustomFieldType.DROPDOWN:
      return <DropdownBorderedIcon />;
    case CustomFieldType.DATE:
      return <CalendarIcon />;
    case CustomFieldType.NUMBER:
      return <HarshIcon />;
    case CustomFieldType.ENTITY_MULTI_SELECTION:
      return <CheckboxActiveIcon bgColor="#F4F5F7" />;
    case CustomFieldType.LABEL:
      return <TagIcon />;
    case CustomFieldType.URL:
      return <LinkTicketIcon isBordered={false} />;
    case CustomFieldType.ENTITY_SINGLE_SELECTION:
      return <CheckedIcon bgColor="#F4F5F7" />;
    case CustomFieldType.TICKET_MULTI_SELECT:
      return <TicketIdIcon />;
    case CustomFieldType.TICKET_SINGLE_SELECT:
      return <TicketIdIcon />;
    case CustomFieldType.RELEASE_SINGLE_SELECT:
      return <ReleaseIdIcon />;
    case CustomFieldType.TEAM_MEMBER_SINGLE_SELECT:
      return <UserIdIcon size={size} />;
    default:
      return null;
    case CustomFieldType.RELEASE_MULTI_SELECT:
      return <ReleaseIdIcon />;
    case CustomFieldType.TEAM_MEMBER_MULTI_SELECT:
      return <UserIdIcon />;
  }
};

const customFieldsTypesFields = [
  {
    title: fieldTypeFields.textInput,
    value: CustomFieldType.TEXT,
    isLink: false,
  },
  {
    title: fieldTypeFields.longTextInput,
    value: CustomFieldType.LONG_TEXT,
    isLink: false,
  },
  {
    title: fieldTypeFields.dropdown,
    value: CustomFieldType.DROPDOWN,
    isLink: false,
  },
  {
    title: fieldTypeFields.date,
    value: CustomFieldType.DATE,
    isLink: false,
  },
  {
    title: fieldTypeFields.number,
    value: CustomFieldType.NUMBER,
    isLink: false,
  },
  {
    title: fieldTypeFields.entitySingleSelect,
    value: CustomFieldType.ENTITY_SINGLE_SELECTION,
    isLink: true,
  },
  {
    title: fieldTypeFields.entityMultiSelect,
    value: CustomFieldType.ENTITY_MULTI_SELECTION,
    isLink: true,
  },
  {
    title: fieldTypeFields.labels,
    value: CustomFieldType.LABEL,
    isLink: false,
  },
  {
    title: fieldTypeFields.url,
    value: CustomFieldType.URL,
    isLink: false,
  },
];
const renderCustomFieldFormValue = (
  loading: boolean,
  form: CustomFieldItem,
  val?: CustomSubMenu,
  ticketType: ITicketTypes[] = [],
): React.ReactNode => {
  const renderLoading = () => {
    return (
      <div className="animate-pulse max-w-[80px] h-[12px]">
        <div className="size-3 rounded bg-[#F4F5F7]  border-[transparent] px-[6px] w-full"></div>
      </div>
    );
  };
  const TextChip = ({
    text,
    className = "text-sm font-medium text-black",
  }: {
    text: string | string[];
    className?: string;
  }) => {
    if (Array.isArray(text)) {
      if (text.length === 0) return null;
      return <span className={className}>{text.join(", ")}</span>;
    }
    if (!text) return null;
    return <span className={className}>{text}</span>;
  };

  const renderTicketTypes = () => {
    const associated = Array.isArray(form?.associatedTicketTypes)
      ? form.associatedTicketTypes
      : [];
    const allTypes = Array.isArray(ticketType) ? ticketType : [];

    // ✅ If all types are associated, show one "All" chip
    if (associated.length > 0 && associated.length === allTypes.length) {
      return <div className="text-sm font-medium text-black ">All</div>;
    }

    // ✅ Render only matched types

    return (
      <RenderTicketTypes
        associatedTicketTypes={form.associatedTicketTypes}
        ticketType={ticketType}
        maxVisible={2}
      />
    );
  };

  // If no submenu key provided, render nothing
  if (!val) return null;

  switch (val) {
    case CustomSubMenu.FIELD_TYPE:
      return loading ? (
        renderLoading()
      ) : (
        <TextChip
          text={form.dataType ?? ""}
          className="text-sm font-medium text-black truncate max-w-[160px]"
        />
      );

    case CustomSubMenu.TICKET_TYPES:
      return loading ? renderLoading() : renderTicketTypes();
    case CustomSubMenu.PERMISSION:
      return "All";

    case CustomSubMenu.DROPDOWN: {
      const options = form.dropDownOptions ?? [];
      if (options.length === 0) return null;

      return loading ? (
        renderLoading()
      ) : (
        <div className="flex items-center gap-1 w-full truncate max-w-[120px]">
          {options.map((o, idx) => (
            <TextChip
              key={String(
                Array.isArray(o.value) ? o.value.join("|") : (o.value ?? idx),
              )}
              text={o.value}
              className="text-sm font-medium text-black "
            />
          ))}
        </div>
      );
    }

    case CustomSubMenu.LABEL: {
      const labels = form.labels ?? [];
      if (labels.length === 0) return null;
      return loading ? (
        renderLoading()
      ) : (
        <div className="flex items-center gap-1 w-full truncate max-w-[120px]">
          {labels.map((l, idx) => (
            <TextChip
              key={String(
                l._id ??
                  (Array.isArray(l.labelName)
                    ? l.labelName.join("|")
                    : (l.labelName ?? idx)),
              )}
              text={l.labelName}
              className="text-sm font-medium text-black"
            />
          ))}
        </div>
      );
    }

    default:
      return null;
  }
};

function getFieldIdByName(
  list: CustomFieldItem[],
  newFieldName: string,
): string | null {
  const target = normalizeFieldName(newFieldName);

  const found = list.find(
    (item) => normalizeFieldName(item.fieldName) === target,
  );

  return found ? found._id : null;
}

function normalizeFieldName(name: string): string {
  return (name ?? "").trim().toLowerCase();
}

export {
  customFieldsTypesFields,
  getCustomFieldsMenus,
  getCustomFieldTypeIcons, getFieldIdByName, headers, renderCustomFieldFormValue
};

